import React, { useState, useMemo, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { 
  Bell, 
  Search, 
  Filter, 
  RefreshCw,
  Building2,
  MapPin,
  DollarSign,
  Clock,
  Shield,
  HardHat,
  Loader2,
  AlertCircle
} from 'lucide-react'
import RfqCard from '../../components/gov/RfqCard'
import RfqDetailModal from '../../components/gov/RfqDetailModal'
import apiService from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { getUserType } from '../../utils/userPermissions'

interface GovRfq {
  id: string
  title: string
  description: string
  budgetRange: string
  location: string
  deadline: string
  attachments: string[]
  createdAt: string
  updatedAt: string
  GovAgency: {
    id: string
    name: string
    agencyType: string
    website?: string
    contactPerson: string
  }
}

interface RfqApiResponse {
  success: boolean
  data: {
    rfqs: GovRfq[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }
}

// Transform API RFQ to RfqCard props format
const transformRfqForCard = (rfq: GovRfq) => ({
  id: rfq.id,
  title: rfq.title,
  agency: rfq.GovAgency?.name || 'Unknown Agency',
  location: rfq.location,
  budget: rfq.budgetRange,
  deadline: rfq.deadline,
  description: rfq.description,
  rfqNumber: `RFQ-${rfq.id ? rfq.id.slice(-8).toUpperCase() : 'UNKNOWN'}`,
  postedDate: rfq.createdAt,
  responseDeadline: rfq.deadline,
  category: rfq.GovAgency?.agencyType || 'Government',
  setAsideProgram: undefined // Not in our current schema
})

export default function GovRfqsPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const userType = getUserType(user)
  
  const [searchTerm, setSearchTerm] = useState('')
  const [budgetFilter, setBudgetFilter] = useState('')
  const [locationFilter, setLocationFilter] = useState('')
  const [deadlineSoon, setDeadlineSoon] = useState(false)
  const [showNotifyModal, setShowNotifyModal] = useState(false)
  const [email, setEmail] = useState('')
  const [page, setPage] = useState(1)
  const [selectedRfqId, setSelectedRfqId] = useState<string | null>(null)

  // ACCESS CONTROL: Only enterprise vendors can access this page
  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }

    // Block enterprise buyers from accessing this page
    if (userType === 'ENTERPRISE_BUYER') {
      navigate('/', { replace: true })
      return
    }

    // Only allow enterprise vendors
    if (userType !== 'ENTERPRISE_VENDOR') {
      navigate('/', { replace: true })
      return
    }
  }, [user, userType, navigate])

  // Show loading while checking access
  if (!user || userType !== 'ENTERPRISE_VENDOR') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking access permissions...</p>
        </div>
      </div>
    )
  }

  // Fetch RFQs from API
  const { 
    data: rfqData, 
    isLoading, 
    error, 
    refetch,
    isRefetching 
  } = useQuery<RfqApiResponse>({
    queryKey: ['public-rfqs', page, budgetFilter, locationFilter, deadlineSoon],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '12'
      })
      
      if (budgetFilter) params.append('budget', budgetFilter)
      if (locationFilter) params.append('location', locationFilter)
      if (deadlineSoon) params.append('deadline_soon', 'true')
      
      const response = await apiService.get(`/api/gov/rfqs/public?${params.toString()}`) as any
      return response
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    // cacheTime: 10 * 60 * 1000 // 10 minutes
  })

  // Transform and filter RFQs
  const rfqs = useMemo(() => {
    if (!rfqData?.data?.rfqs) return []
    return rfqData.data.rfqs.map(transformRfqForCard)
  }, [rfqData])

  // Apply client-side search filter
  const filteredRfqs = useMemo(() => {
    if (!searchTerm) return rfqs
    
    return rfqs.filter(rfq => 
      rfq.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.agency.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rfq.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [rfqs, searchTerm])

  // Calculate stats
  const stats = useMemo(() => {
    const totalValue = rfqs.reduce((sum, rfq) => {
      // Extract numeric value from budget range (simplified)
      const match = rfq.budget.match(/\$([0-9,]+)/)
      const value = match ? parseInt(match[1].replace(',', '')) : 0
      return sum + value
    }, 0)

    const locations = new Set(rfqs.map(rfq => rfq.location.split(',')[1]?.trim() || rfq.location))
    
    return {
      activeRfqs: filteredRfqs.length,
      totalValue: totalValue > 1000000 ? `$${(totalValue / 1000000).toFixed(1)}M+` : `$${Math.round(totalValue / 1000)}K+`,
      locations: locations.size,
      avgResponseTime: '15 Days' // Static for now
    }
  }, [filteredRfqs, rfqs])

  const handleNotifySubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // TODO: Integrate with email service
    console.log('Email notification signup:', email)
    setShowNotifyModal(false)
    setEmail('')
    toast.success('Thank you! We\'ll notify you about new RFQs matching your interests.')
  }

  const handleRefresh = () => {
    refetch()
  }

  const clearFilters = () => {
    setSearchTerm('')
    setBudgetFilter('')
    setLocationFilter('')
    setDeadlineSoon(false)
    setPage(1)
  }

  const handleViewDetails = (rfqId: string) => {
    setSelectedRfqId(rfqId)
  }

  const handleCloseModal = () => {
    setSelectedRfqId(null)
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to Load RFQs</h2>
          <p className="text-gray-600 mb-6">
            We're having trouble loading the government RFQs. Please try again.
          </p>
          <button 
            onClick={handleRefresh}
            className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Government Solar RFQs
              </h1>
              <p className="mt-2 text-gray-600">
                Discover government contracting opportunities in the solar energy sector
              </p>
              
              {/* Navigation Tabs */}
              <div className="mt-6 border-t border-gray-200 pt-6">
                <nav className="flex space-x-8">
                  <Link 
                    to="/enterprise" 
                    className="flex items-center space-x-2 pb-2 border-b-2 border-transparent hover:border-primary-600 text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    <HardHat className="h-5 w-5" />
                    <span className="font-medium">Vendor Directory</span>
                  </Link>
                  <div className="flex items-center space-x-2 pb-2 border-b-2 border-emerald-600">
                    <Shield className="h-5 w-5 text-emerald-600" />
                    <span className="font-medium text-emerald-600">Government RFQs</span>
                  </div>
                </nav>
              </div>
              
              {/* Government Certification Status */}
              <div className="mt-4">
                <div className={`inline-flex items-center px-4 py-2 rounded-lg border ${
                  user?.isGovCertified 
                    ? 'bg-green-50 border-green-200 text-green-800' 
                    : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                }`}>
                  <Shield className="h-4 w-4 mr-2" />
                  {user?.isGovCertified ? (
                    <span className="text-sm font-medium">
                      ✅ Government Certified - You can submit proposals to all RFQs
                    </span>
                  ) : (
                    <span className="text-sm font-medium">
                      ⚠️ Viewing Only - Get government certification to submit proposals
                    </span>
                  )}
                  {!user?.isGovCertified && (
                    <Link 
                      to="/dashboard?tab=government" 
                      className="ml-3 text-sm font-medium text-yellow-700 hover:text-yellow-900 underline"
                    >
                      Apply for Certification
                    </Link>
                  )}
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setShowNotifyModal(true)}
              className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
            >
              <Bell className="h-5 w-5 mr-2" />
              Notify me about new RFQs
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-emerald-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center">
              <Building2 className="h-5 w-5 text-emerald-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Active RFQs</p>
                <p className="font-bold text-gray-900">{isLoading ? '...' : stats.activeRfqs}</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <DollarSign className="h-5 w-5 text-emerald-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Total Value</p>
                <p className="font-bold text-gray-900">{isLoading ? '...' : stats.totalValue}</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <MapPin className="h-5 w-5 text-emerald-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Locations</p>
                <p className="font-bold text-gray-900">{isLoading ? '...' : `${stats.locations} States`}</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-emerald-600 mr-2" />
              <div>
                <p className="text-sm text-gray-600">Avg. Response Time</p>
                <p className="font-bold text-gray-900">{stats.avgResponseTime}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search RFQs by title, agency, location, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            
            {/* Budget Filter */}
            <div className="sm:w-48">
              <select
                value={budgetFilter}
                onChange={(e) => setBudgetFilter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">All Budgets</option>
                <option value="Under $50,000">Under $50K</option>
                <option value="$50,000 - $100,000">$50K - $100K</option>
                <option value="$100,000 - $250,000">$100K - $250K</option>
                <option value="$250,000 - $500,000">$250K - $500K</option>
                <option value="$500,000 - $1,000,000">$500K - $1M</option>
                <option value="Over $1,000,000">Over $1M</option>
              </select>
            </div>

            {/* Location Filter */}
            <div className="sm:w-48">
              <input
                type="text"
                placeholder="Filter by location"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Additional Filters */}
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deadlineSoon}
                  onChange={(e) => setDeadlineSoon(e.target.checked)}
                  className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                />
                <span className="text-sm text-gray-700">Deadline within 30 days</span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <button 
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Clear Filters
              </button>
              <button 
                onClick={handleRefresh}
                disabled={isRefetching}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            {isLoading ? 'Loading...' : (
              <>
                Showing {filteredRfqs.length} of {rfqs.length} RFQs
                {searchTerm && ` for "${searchTerm}"`}
                {(budgetFilter || locationFilter || deadlineSoon) && ' (filtered)'}
              </>
            )}
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading government RFQs...</p>
            </div>
          </div>
        )}

        {/* RFQ Grid */}
        {!isLoading && filteredRfqs.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {filteredRfqs.map(rfq => (
              <RfqCard 
                key={rfq.id} 
                rfq={rfq} 
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredRfqs.length === 0 && rfqs.length === 0 && (
          <div className="text-center py-12">
            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No RFQs Available</h3>
            <p className="text-gray-600 mb-6">
              There are currently no active government RFQs. Check back soon for new opportunities.
            </p>
            <button 
              onClick={handleRefresh}
              className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        )}

        {/* No Results State */}
        {!isLoading && filteredRfqs.length === 0 && rfqs.length > 0 && (
          <div className="text-center py-12">
            <Filter className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No RFQs found</h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your search or filter criteria to find relevant opportunities.
            </p>
            <button 
              onClick={clearFilters}
              className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && rfqData?.data?.pagination && rfqData.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-center space-x-4 mt-8">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-gray-600">
              Page {page} of {rfqData.data.pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(rfqData.data.pagination.totalPages, p + 1))}
              disabled={page === rfqData.data.pagination.totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* RFQ Detail Modal */}
      {selectedRfqId && (
        <RfqDetailModal 
          rfqId={selectedRfqId}
          isOpen={!!selectedRfqId}
          onClose={handleCloseModal}
        />
      )}

      {/* Notification Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Get Notified About New RFQs
            </h3>
            
            <form onSubmit={handleNotifySubmit}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              
              <p className="text-sm text-gray-600 mb-6">
                We'll send you notifications when new solar RFQs are posted that match your interests.
              </p>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowNotifyModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                >
                  Subscribe
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
} 